<template>
	<div class="flex flex-col gap-8">
		<div>
			<h1 class="text-2xl font-serif">{{ $flowstore.checkout.meta.text.customer.title }}</h1>
			<p>{{ $flowstore.checkout.meta.text.customer.description }}</p>
		</div>

		<ForgotPasswordModal ref="forgotPasswordModal" />

		<div v-if="showCancellationGuarantee" class="max-w-2xl bg-white shadow-card rounded p-6 lg:p-8">
			<h2 class="text-xl font-serif mb-4">{{ t('cancellation_guarantee_title') }}</h2>
			<CheckField v-model="cancellationGuaranteeChecked">
				<span class="font-semibold">
					{{ t('cancellation_guarantee_checkbox_label', { price: cancellationGuarantee?.price ?? '' }) }}
				</span>
				<span class="block mt-1 text-sm text-qwr-dark-600">
					{{ t('cancellation_guarantee_subtitle') }}
					<ToolTip
						on-hover
						class="align-text-bottom ml-1 translate-y-1.5"
						data-cy="cancellation-guarantee-tooltip"
						:aria-label="t('cancellation_guarantee_title')"
					>
						<template #toggle>
							<InfoIcon class="w-5" />
						</template>
						<p class="mb-4 font-semibold">
							{{ t('cancellation_guarantee_description', { price: cancellationGuarantee?.price ?? '' }) }}
						</p>
						<ul class="space-y-2 mb-4">
							<li class="flex items-start gap-3">
								<PlusIcon class="text-gold-500 size-4.5 flex-none mt-0.5" />
								<span>{{ t('cancellation_guarantee_benefit_any_reason') }}</span>
							</li>
							<li class="flex items-start gap-3">
								<PlusIcon class="text-gold-500 size-4.5 flex-none mt-0.5" />
								<span>{{ t('cancellation_guarantee_benefit_deadline') }}</span>
							</li>
							<li class="flex items-start gap-3">
								<PlusIcon class="text-gold-500 size-4.5 flex-none mt-0.5" />
								<span>{{ t('cancellation_guarantee_benefit_reschedule') }}</span>
							</li>
						</ul>
						<p>{{ t('cancellation_guarantee_online_only') }}</p>
					</ToolTip>
				</span>
			</CheckField>
		</div>

		<div>
			<h2 class="text-xl font-serif mb-4">{{ t('how_would_you_like_to_book_your_relaxing_moment') }}</h2>
			<div class="flex flex-col gap-4 mb-10">
				<RadioField
					v-model="$customerstore.authentication.action"
					group="action"
					on-value="login"
					:errors="$validationstore.errors['authentication.action']"
				>
					{{ t('i_have_a_my_thermen_account') }}
				</RadioField>
				<RadioField
					v-model="$customerstore.authentication.action"
					group="action"
					on-value="register"
					:errors="$validationstore.errors['authentication.action']"
				>
					{{ t('i_want_to_create_a_my_thermen_account') }}
					<ToolTip on-hover>{{ t('i_want_to_create_a_my_thermen_account_tooltip') }}</ToolTip>
				</RadioField>
				<RadioField
					v-model="$customerstore.authentication.action"
					group="action"
					on-value="guest"
					:errors="$validationstore.errors['authentication.action']"
				>
					{{ t('i_want_to_make_a_reservation_without_an_account') }}
				</RadioField>
			</div>

			<form
				v-if="$customerstore.authentication.action === 'login'"
				method="post"
				class="my-10"
				@submit.prevent="loginCustomer"
			>
				<LoadingOverlay v-if="$loadingstore.login" />

				<div v-if="!isAuthenticated" class="grid grid-cols-12 gap-6">
					<TextField
						v-model="$customerstore.authentication.email"
						name="username"
						:errors="$validationstore.errors['email']"
						class="col-span-full sm:col-span-6 input-required"
					>
						{{ t('email_address') }}
					</TextField>
					<PasswordField
						v-model="$customerstore.authentication.password"
						name="password"
						:errors="$validationstore.errors['password']"
						class="col-span-full sm:col-span-6 input-required"
					>
						{{ t('password') }}
					</PasswordField>
				</div>

				<div class="mt-4">
					<div v-if="!isAuthenticated && $validationstore.errors['authentication']" class="text-sm text-qwr-dark-600">
						{{ t('forgot_your_password_no_problem') }}
						<button type="button" class="text-sm underline text-primary text-left" @click="forgotPasswordModal?.openModal()">
							{{ t('request_your_new_password_here') }}
						</button>
					</div>
					<ErrorBag is-notice :errors="$validationstore.errors['forgot_password']" />
				</div>

				<div class="flex justify-between mt-6">
					<ActionButton v-if="!isAuthenticated" type="submit" @click="loginCustomer">{{ t('login') }}</ActionButton>
					<ActionButton v-if="isAuthenticated" @click="logoutCustomer">{{ t('logout') }}</ActionButton>
					<ErrorBag :errors="$validationstore.errors['authentication']" />
				</div>
			</form>

			<Transition name="fade">
				<NoteBlock
					v-if="validationStore.errorsExceptFlat(catchAllErrorsExcept).length"
					variant="error"
					class="my-6 error-scroll-anchor"
				>
					<div v-for="(message, i) in validationStore.errorsExceptFlat(catchAllErrorsExcept)" :key="`error_m_${i}`">
						{{ message }}
					</div>
				</NoteBlock>
			</Transition>
		</div>

		<template v-if="customerFormVisible">
			<template v-if="$customerstore.authentication.action === 'register'">
				<div class="grid grid-cols-12 gap-6">
					<RadioField
						v-model="$customerstore.personal.gender"
						group="gender"
						on-value="male"
						:errors="$validationstore.errors['personal_data.gender']"
						class="col-span-4"
					>
						{{ t('mr') }}
					</RadioField>
					<RadioField
						v-model="$customerstore.personal.gender"
						group="gender"
						on-value="female"
						:errors="$validationstore.errors['personal_data.gender']"
						class="col-span-4"
					>
						{{ t('ms') }}
					</RadioField>
					<RadioField
						v-model="$customerstore.personal.gender"
						group="gender"
						on-value="other"
						:errors="$validationstore.errors['personal_data.gender']"
						class="col-span-4"
					>
						{{ t('other') }}
					</RadioField>

					<ErrorBag :errors="$validationstore.errors['personal_data.gender']" class="col-span-full -mt-5" />
				</div>
			</template>

			<div class="grid grid-cols-12 gap-6">
				<template v-if="!hasSelectedGermany">
					<TextField
						v-model="$customerstore.personal.first_name"
						name="first_name"
						autocomplete="given-name"
						:errors="$validationstore.errors['personal_data.first_name']"
						class="col-span-full lg:col-span-5 input-required"
					>
						{{ t('first_name') }}
					</TextField>
					<TextField
						v-model="$customerstore.personal.infix"
						name="infix"
						class="col-span-4 lg:col-span-3"
						autocomplete="additional-name"
					>
						{{ t('name_infix') }}
					</TextField>
					<TextField
						v-model="$customerstore.personal.last_name"
						name="last_name"
						autocomplete="family-name"
						:errors="$validationstore.errors['personal_data.last_name']"
						class="col-span-8 lg:col-span-4 input-required"
					>
						{{ t('last_name') }}
					</TextField>
					<TextField
						v-model="$customerstore.personal.address.zip_code"
						name="zip_code"
						autocomplete="postal-code"
						:errors="$validationstore.errors['personal_data.address.zip_code']"
						class="col-span-4 lg:col-span-3 input-required"
					>
						{{ t('zip_code') }}
					</TextField>
					<NumberField
						v-model="$customerstore.personal.address.house_number"
						name="house_number"
						autocomplete="off"
						:errors="$validationstore.errors['personal_data.address.house_number']"
						class="col-span-4 lg:col-span-3 input-required"
					>
						{{ t('house_number') }}
					</NumberField>
					<AutoCompleteHoneyPotField type="address-line2" />
					<TextField
						v-model="$customerstore.personal.address.house_number_addition"
						name="house_number_addition"
						autocomplete="off"
						class="col-span-4 lg:col-span-2"
					>
						{{ t('house_number_addition') }}
					</TextField>
					<SelectField
						v-model="$customerstore.personal.address.country"
						name="country"
						autocomplete="country"
						:errors="$validationstore.errors['personal_data.address.country']"
						:options="countries"
						class="col-span-full lg:col-span-4 input-required"
					>
						{{ t('country') }}
					</SelectField>
					<AutoCompleteHoneyPotField type="country-name" />
					<TransitionGroup name="fade">
						<TextField
							v-if="showPersonalAddressFields"
							v-model="$customerstore.personal.address.street"
							name="street"
							autocomplete="address-line1"
							:errors="$validationstore.errors['personal_data.address.street']"
							class="col-span-full lg:col-span-6 input-required"
						>
							{{ t('street') }}
						</TextField>
						<TextField
							v-if="showPersonalAddressFields"
							v-model="$customerstore.personal.address.city"
							name="city"
							autocomplete="address-level2"
							:errors="$validationstore.errors['personal_data.address.city']"
							class="col-span-full lg:col-span-6 input-required"
						>
							{{ t('city') }}
						</TextField>
					</TransitionGroup>
					<TextField
						v-model="$customerstore.personal.phone_number"
						name="phone_number"
						:errors="$validationstore.errors['personal_data.phone_number']"
						class="col-span-full lg:col-span-6 input-required"
					>
						{{ t('mobile_phone_number') }}
					</TextField>
					<TextField
						v-model="$customerstore.personal.email"
						name="email"
						:disabled="isAuthenticated"
						autocomplete="email"
						:errors="$validationstore.errors['personal_data.email']"
						class="col-span-full lg:col-span-6 input-required"
					>
						{{ t('email_address') }}
					</TextField>
				</template>

				<template v-else-if="hasSelectedGermany">
					<TextField
						v-model="$customerstore.personal.first_name"
						name="first_name"
						autocomplete="given-name"
						:errors="$validationstore.errors['personal_data.first_name']"
						class="col-span-full lg:col-span-6 input-required"
					>
						{{ t('first_name') }}
					</TextField>
					<TextField
						v-model="$customerstore.personal.last_name"
						name="last_name"
						autocomplete="family-name"
						:errors="$validationstore.errors['personal_data.last_name']"
						class="col-span-full lg:col-span-6 input-required"
					>
						{{ t('last_name') }}
					</TextField>
					<TextField
						v-model="$customerstore.personal.address.street"
						name="street"
						:errors="$validationstore.errors['personal_data.address.street']"
						class="col-span-full lg:col-span-6 input-required"
					>
						{{ t('street') }}
					</TextField>
					<NumberField
						v-model="$customerstore.personal.address.house_number"
						name="house_number"
						autocomplete="off"
						:errors="$validationstore.errors['personal_data.address.house_number']"
						class="col-span-4 lg:col-span-3 input-required"
					>
						{{ t('house_number') }}
					</NumberField>
					<AutoCompleteHoneyPotField type="address-line2" />
					<TextField
						v-model="$customerstore.personal.address.house_number_addition"
						name="house_number_addition"
						autocomplete="off"
						class="col-span-4 lg:col-span-3"
					>
						{{ t('house_number_addition') }}
					</TextField>
					<TextField
						v-model="$customerstore.personal.address.zip_code"
						name="zip_code"
						autocomplete="postal-code"
						:errors="$validationstore.errors['personal_data.address.zip_code']"
						class="col-span-4 lg:col-span-6 input-required"
					>
						{{ t('zip_code') }}
					</TextField>
					<TextField
						v-model="$customerstore.personal.address.city"
						name="city"
						:errors="$validationstore.errors['personal_data.address.city']"
						class="col-span-full lg:col-span-3 input-required"
						autocomplete="address-level2"
					>
						{{ t('city') }}
					</TextField>
					<SelectField
						v-model="$customerstore.personal.address.country"
						name="country"
						autocomplete="country"
						:errors="$validationstore.errors['personal_data.address.country']"
						:options="countries"
						class="col-span-full lg:col-span-3 input-required"
					>
						{{ t('country') }}
					</SelectField>
					<AutoCompleteHoneyPotField type="country-name" />
					<TextField
						v-model="$customerstore.personal.phone_number"
						name="phone_number"
						:errors="$validationstore.errors['personal_data.phone_number']"
						class="col-span-full lg:col-span-6 input-required"
					>
						{{ t('mobile_phone_number') }}
					</TextField>
					<TextField
						v-model="$customerstore.personal.email"
						name="email"
						autocomplete="email"
						:errors="$validationstore.errors['personal_data.email']"
						class="col-span-full lg:col-span-6 input-required"
					>
						{{ t('email_address') }}
					</TextField>
				</template>
			</div>

			<div v-if="$customerstore.authentication.action === 'register'" class="grid grid-cols-12 gap-6">
				<PasswordField
					v-model="$customerstore.authentication.password"
					name="password"
					autocomplete="new-password"
					:errors="$validationstore.errors['authentication.password']"
					class="col-span-full lg:col-span-6 input-required"
				>
					{{ t('password') }}
				</PasswordField>
				<PasswordField
					v-model="$customerstore.authentication.password_confirmation"
					name="password_confirmation"
					autocomplete="new-password"
					:errors="$validationstore.errors['authentication.password_confirmation']"
					class="col-span-full lg:col-span-6 input-required"
				>
					{{ t('password_confirmation') }}
				</PasswordField>
			</div>

			<template v-if="$customerstore.authentication.action === 'register'">
				<CollapsibleCard icon="gift" :title="t('receive_a_present_on_your_birthday')" :opened="hasBirthDate">
					<h4 class="font-semibold">{{ t('date_of_birth') }}:</h4>
					<div class="grid lg:grid-cols-8 gap-6">
						<SelectField
							v-model="birth_date_day"
							class="lg:col-span-2"
							variant="outlined"
							:options="days"
							:placeholder="t('day')"
						/>
						<SelectField
							v-model="birth_date_month"
							class="lg:col-span-3"
							variant="outlined"
							:options="months"
							:placeholder="t('month')"
						/>
						<SelectField
							v-model="birth_date_year"
							class="lg:col-span-3"
							variant="outlined"
							:options="years"
							:placeholder="t('year')"
						/>
					</div>
				</CollapsibleCard>
			</template>

			<CollapsibleCard icon="mail" :title="commentFieldTitle" :opened="commentFieldDefaultOpen" @open="focusCommentField">
				<TextareaField ref="commentField" v-model="$customerstore.comment" />
			</CollapsibleCard>

			<CheckField v-model="$customerstore.terms" :errors="$validationstore.errors['terms']">
				<div>
					<p class="mb-1">{{ t('accept_terms_sentence') }}</p>
					<ul class="list-disc pl-4 space-y-0.5">
						<li v-if="$flowstore.hasTreatmentOrRitual">{{ t('terms_item_1') }}</li>
						<i18n-t tag="li" keypath="terms_item_2">
							<template #house_rules>
								<a
									:href="$flowstore.checkout.meta.links?.house_rules"
									target="_blank"
									class="text-primary hover:underline focus:outline-hidden focus:underline"
								>
									{{ t('house_rules') }}
								</a>
							</template>
						</i18n-t>
						<i18n-t tag="li" keypath="terms_item_3">
							<template #terms_and_conditions>
								<a
									:href="$flowstore.checkout.meta.links?.terms_and_conditions"
									target="_blank"
									class="text-primary hover:underline focus:outline-hidden focus:underline"
								>
									{{ t('terms_and_conditions') }}
								</a>
							</template>
							<template #cancellation_policy>
								<a
									:href="$flowstore.checkout.meta.links?.cancellation_policy"
									target="_blank"
									class="text-primary hover:underline focus:outline-hidden focus:underline"
								>
									{{ t('cancellation_policy') }}
								</a>
							</template>
							<template #privacy_policy>
								<a
									:href="$flowstore.checkout.meta.links?.privacy_policy"
									target="_blank"
									class="text-primary hover:underline focus:outline-hidden focus:underline"
								>
									{{ t('privacy_policy') }}
								</a>
							</template>
						</i18n-t>
					</ul>
				</div>
			</CheckField>

			<CheckField v-model="$customerstore.personal.opt_in">
				{{ t('accept_mailinglist_sentence', { resort: thermenSites[config.theme] }) }}
			</CheckField>

			<CheckField v-if="$flowstore.checkout.meta.product.is_workshop" v-model="$customerstore.personal.invoice.opt_in">
				{{ t('accept_retreats_mailinglist_sentence') }}
			</CheckField>
		</template>

		<!-- <CheckField v-if="$customerstore.personal.address.country?.toUpperCase() === 'NL'" v-model="$customerstore.personal.thermen_fans">
      <i18n-t tag="div" keypath="accept_thermenfans_sentence">
        <template #link>
          <a href="#" target="_blank" class="text-primary hover:underline focus:outline-hidden focus:underline">{{ t('thermenfans_program') }}</a>
        </template>
      </i18n-t>
    </CheckField> -->
	</div>
</template>

<script lang="ts" setup>
import { toNumber } from '@/stores/utils';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { config, dateLocale } from '@/config';
import { format, isValid, parse } from 'date-fns';
import { lookupAddress } from '@/api/endpoints/checkout';
import { storeToRefs } from 'pinia';
import { useCustomerStore } from '@/stores/useCustomerStore';
import { useFlowStore } from '@/stores/useFlowStore';
import { useGTMStore } from '@/stores/useGTMStore';
import { useI18n } from 'vue-i18n';
import { useValidationStore } from '@/stores/useValidationStore';
import ActionButton from '@/components/ActionButton.vue';
import AutoCompleteHoneyPotField from '@/components/fields/AutoCompleteHoneyPotField.vue';
import CheckField from '@/components/fields/CheckField.vue';
import CollapsibleCard from '@/components/CollapsibleCard.vue';
import ErrorBag from '@/components/ErrorBag.vue';
import LoadingOverlay from '@/components/LoadingOverlay.vue';
import NumberField from '@/components/fields/NumberField.vue';
import PasswordField from '@/components/fields/PasswordField.vue';
import RadioField from '@/components/fields/RadioField.vue';
import SelectField from '@/components/fields/SelectField.vue';
import TextareaField from '@/components/fields/TextareaField.vue';
import TextField from '@/components/fields/TextField.vue';
import ToolTip from '@/components/ToolTip.vue';
import ForgotPasswordModal from '@/components/modals/ForgotPasswordModal.vue';
import NoteBlock from '@/components/NoteBlock.vue';
import PlusIcon from '@thermenresorts/icons/svg/plus.svg';
import InfoIcon from '@thermenresorts/icons/svg/info.svg';
import type { PersonalDataAddressResource } from '@/api/resources';

const { t } = useI18n();
const customerStore = useCustomerStore();
const flowStore = useFlowStore();
const gtmStore = useGTMStore();
const validationStore = useValidationStore();

const { hasBirthDate, hasComment, personal, isAuthenticated } = storeToRefs(customerStore);
const forgotPasswordModal = ref<InstanceType<typeof ForgotPasswordModal>>();

const birth_date_day = ref();
const birth_date_month = ref();
const birth_date_year = ref();
const commentField = ref<InstanceType<typeof TextareaField>>();

const catchAllErrorsExcept = [
  'authentication.action',
  'authentication.password_confirmation',
  'authentication.password',
  'authentication',
  'email',
  'forgot_password',
  'password',
  'personal_data.address.city',
  'personal_data.address.country',
  'personal_data.address.house_number',
  'personal_data.address.street',
  'personal_data.address.zip_code',
  'personal_data.email',
  'personal_data.first_name',
  'personal_data.gender',
  'personal_data.last_name',
  'personal_data.phone_number',
  'terms',
];

const thermenSites = {
  bussloo: 'Bussloo',
  soesterberg: 'Soesterberg',
  berendonck: 'Berendonck',
  badnieuweschans: 'Bad Nieuweschans',
  maarssen: 'Maarssen',
  maastricht: 'Maastricht',
}

onMounted(() => {
  if (personal.value.birth_date) {
    const safeBirthDate = personal.value.birth_date.substring(0, 10);
    const date = parse(safeBirthDate, 'yyyy-MM-dd', new Date());
    birth_date_year.value = format(date, 'yyyy');
    birth_date_month.value = format(date, 'M');
    birth_date_day.value = format(date, 'd');
  }

  if (personal.value.address.country && !countryCodes.value.includes(personal.value.address.country)) {
    personal.value.address.country = 'OT';
  }
});

watch([birth_date_year, birth_date_month, birth_date_day], values => {
  const date = parse(values.join('-'), 'yyyy-MM-dd', new Date());
  if (isValid(date)) {
    personal.value.birth_date = format(date, 'yyyy-MM-dd');
  }
});

const hasSelectedGermany = computed(() => personal.value.address.country?.toUpperCase() === 'DE');

watch(() => validationStore.errors, async () => {
  await nextTick();
  document.querySelector('.error-scroll-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
});

const showPersonalAddressFields = computed(() => personal.value.address.attempted_lookup || hasAddressFieldData(personal.value.address));

watch(() => [personal.value.address.zip_code, personal.value.address.house_number], async ([zipCode, houseNumber]) => {
  await fillAddress(personal.value.address, zipCode, houseNumber);
});

async function fillAddress(address: PersonalDataAddressResource | null, zipCode: string | null, houseNumber: string | null) {
  if (address === null) return;
  if (isEmpty(address.city) && isEmpty(address.street)) {
			if (isEmpty(zipCode)) {
					address.attempted_lookup = false;

					return;
			}

    const _houseNumber = toNumber(houseNumber);
    if (!_houseNumber) return;

    const response = await lookupAddress(`${zipCode}`, _houseNumber);
    if (response && response.data) {
      address.street = response.data.street;
      address.city = response.data.city;
      address.country = 'NL';
    }

				address.attempted_lookup = true;
  }
}

const isEmpty = (text: string | null) => !text || text.trim().length === 0;
const hasAddressFieldData = (address: PersonalDataAddressResource) => !isEmpty(address.street) || !isEmpty(address.city);

const loginCustomer = () => {
  customerStore.login()
};
const logoutCustomer = () => {
  customerStore.logout()
};

const focusCommentField = () => {
  commentField.value?.focus();
};

const customerFormVisible = computed(() => {
  if (customerStore.authentication.action === 'login' && isAuthenticated.value === false) {
    return false;
  }
  return true;
});

const commentFieldTitle = computed(() => {
  if (flowStore.checkout.meta.overwrite_comment_field && flowStore.checkout.meta.overwritten_comment_text) {
    return flowStore.checkout.meta.overwritten_comment_text;
  }
  return t('comments_or_extra_information');
});

const cancellationGuarantee = computed(() => flowStore.checkout.meta.cancellation_guarantee);

const showCancellationGuarantee = computed(() => {
  return !!cancellationGuarantee.value;
});


const cancellationGuaranteeChecked = computed({
  get: () => {
    const selectedPersonIds = cancellationGuarantee.value?.selected_person_ids ?? [];
    return selectedPersonIds.length > 0 && selectedPersonIds.length === flowStore.checkout.data.persons_count;
  },
  set: async (checked: boolean) => {
    const guarantee = cancellationGuarantee.value;
    if (!guarantee) {
      return;
    }

    if (checked) {
      const personIds = Array.from({ length: flowStore.checkout.data.persons_count }, (_, index) => index + 1);
      await flowStore.setPersonChoices(personIds, 'cancellation.guarantee.products', guarantee.product_id);

      gtmStore.sendEvent('add_cancellation', {}, true);

      return;
    }

    for (const personId of guarantee.selected_person_ids) {
      await flowStore.deletePersonChoice(personId, 'cancellation.guarantee.products', guarantee.product_id);
    }

    gtmStore.sendEvent('skip_cancellation', {}, true);
  },
});

const commentFieldDefaultOpen = computed(() => {
  if (hasComment.value) return true;
  if (flowStore.checkout.meta.expand_comment_field) return true;
  return false;
});

const countries = computed(() => {
  const mapped = [];
  for (const code in flowStore.checkout.meta.countries) {
    mapped.push({ label: flowStore.checkout.meta.countries[code], value: code });
  }
  return mapped;
});

const countryCodes = computed(() => countries.value.map(country => country.value));

const days = ref(Array.from({ length: 31 }, (_, day) => ({
  label: String(day + 1),
  value: String(day + 1),
})));

const months = ref(
  [...Array(12).keys()].map(index => ({
    label: dateLocale.localize?.month(index),
    value: String(index + 1),
  }))
);

const today = new Date();
const startYear = today.getFullYear();

const years = ref(Array.from({ length: 110 }, (_, year) => ({
  label: String(startYear - year),
  value: String(startYear - year),
})));
</script>
